import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { CheckCircle, ChatCircle, PaperPlaneTilt, ShieldCheck, Warning, DownloadSimple, ClockCounterClockwise } from "@phosphor-icons/react";
import { SafeHtml } from "@/lib/safeHtml";

// Public spec review page — reached via the encrypted share_token URL
// emailed to the client. No login required; all state comes from the
// server by token.
const API = process.env.REACT_APP_BACKEND_URL;

const fmtShort = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(d).slice(0, 10); }
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(d).slice(0, 16); }
};

export default function PublicSpecPage() {
  const { token } = useParams();
  const [spec, setSpec] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/specs/public/${token}`),
      axios.get(`${API}/api/specs/public/${token}/history`).catch(() => ({ data: [] })),
    ])
      .then(([s, h]) => { setSpec(s.data); setHistory(h.data || []); })
      .catch(e => setError(e.response?.data?.detail || "Unable to load spec"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const approve = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/specs/public/${token}/approve`);
      toast.success("Thank you — the specification is now approved.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to approve"); }
    setSubmitting(false);
  };

  const sendBack = async () => {
    if (!comment.trim()) return toast.error("Please enter a comment before sending back");
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/specs/public/${token}/return`, { comment });
      toast.success("Thank you — your comments have been sent to Eyvo.");
      setShowComment(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to send"); }
    setSubmitting(false);
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/api/specs/public/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Eyvo_Spec_${(spec.title || "spec").replace(/[^a-z0-9\- _]/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><div className="w-10 h-10 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <div className="bg-white border border-[#E5E5E5] p-10 max-w-lg text-center">
        <Warning size={48} className="text-[#DC2626] mx-auto mb-4" weight="duotone" />
        <h1 className="text-xl font-black text-[#0A0A0A] mb-2">Link Unavailable</h1>
        <p className="text-sm text-[#525252]">{error}</p>
        <p className="text-xs text-[#A3A3A3] mt-4">If you believe this is in error, please reply to the email from Eyvo.</p>
      </div>
    </div>
  );

  const isDecided = spec.status === "client_approved" || spec.status === "client_returned";
  const statusLabel = spec.status === "client_approved" ? "Approved" :
                      spec.status === "client_returned" ? "Returned with Comments" :
                      "Awaiting Your Decision";
  const statusColor = spec.status === "client_approved" ? "#059669" :
                      spec.status === "client_returned" ? "#D97706" : "#0055FF";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-spec-page">
      <Toaster position="top-right" richColors />

      {/* Subtle confidential notice — red text on white, small, centered, two lines. */}
      <div className="bg-white text-center py-3 px-4 text-[11px] font-bold text-[#DC2626] leading-tight tracking-[1px] break-words" data-testid="confidential-top">
        CONFIDENTIAL<br />
        Not for distribution outside of {spec.client_name}
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header with logo + spec number */}
        <div className="flex items-start justify-between mb-8">
          <img src={`${API}/api/files/email/eyvo-logo.png`} alt="Eyvo" className="h-12" />
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[1.5px] text-[#0055FF] font-mono">{spec.spec_number}</p>
            <p className="text-[10px] font-bold uppercase tracking-[1.5px] text-[#A3A3A3] mt-1">Specification</p>
          </div>
        </div>

        <div className="border-b-[3px] border-[#0055FF] mb-6" />

        {/* Prepared for + date */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Prepared For</p>
            <p className="text-base font-bold text-[#0A0A0A] mt-1 break-words">{spec.client_name}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Sent</p>
            <p className="text-sm text-[#0A0A0A] mt-1">{spec.sent_at ? spec.sent_at.slice(0, 10) : "—"}</p>
          </div>
        </div>

        {/* Status strip */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <span
            className="text-[10px] font-black uppercase tracking-[1.5px] px-2.5 py-1 border"
            style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", borderColor: "#BFDBFE" }}
            data-testid="public-version-badge"
          >
            Version <span className="normal-case">v{spec.current_version || 1}</span>
          </span>
          <span className="text-[9px] font-bold uppercase tracking-[1.5px] px-2.5 py-1" style={{ backgroundColor: statusColor + "15", color: statusColor }}>
            {statusLabel}
          </span>
          {history.length > 1 && (
            <button
              onClick={() => setShowTimeline(v => !v)}
              className="text-[10px] font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] flex items-center gap-1"
              data-testid="public-toggle-timeline"
            >
              <ClockCounterClockwise size={12} />
              {showTimeline ? "Hide" : "View"} iteration history ({history.length} versions)
            </button>
          )}
          {spec.last_comment && spec.status === "client_returned" && (
            <span className="text-[11px] text-[#A3A3A3]">Comments on file with Eyvo</span>
          )}
        </div>

        {/* Iteration timeline — only shown after the first iteration and
            only when the client toggles it open. Keeps the default view
            focused on the current document. */}
        {showTimeline && history.length > 0 && (
          <div className="mb-6 bg-white border border-[#E5E5E5] p-5" data-testid="public-timeline">
            <p className="text-[10px] font-black uppercase tracking-[1.5px] text-[#A3A3A3] mb-4">Iteration history</p>
            <ol className="space-y-4">
              {history.map((h) => {
                const isCurrent = h.version === (spec.current_version || 1);
                return (
                  <li key={h.version} className={`flex gap-4 pb-4 ${h.version !== history[history.length - 1].version ? "border-b border-[#F5F5F5]" : ""}`}>
                    <div className={`flex-shrink-0 w-12 h-12 flex items-center justify-center font-black text-sm ${isCurrent ? "bg-[#0055FF] text-white" : "bg-[#F5F5F5] text-[#525252]"}`} data-testid={`public-timeline-v${h.version}`}>
                      v{h.version}
                    </div>
                    <div className="flex-1 min-w-0 text-xs text-[#525252]">
                      <p><strong className="text-[#0A0A0A]">Sent</strong> · {fmtDateTime(h.sent_at)}</p>
                      {h.first_opened_at && <p>Opened by you · {fmtDateTime(h.first_opened_at)}</p>}
                      {h.client_returned_at && (
                        <p className="text-[#D97706] font-bold">↩ You sent it back with comments · {fmtDateTime(h.client_returned_at)}</p>
                      )}
                      {h.client_approved_at && (
                        <p className="text-[#059669] font-bold">★ You approved this version · {fmtDateTime(h.client_approved_at)}</p>
                      )}
                      {h.client_comment && (
                        <div className="mt-2 p-2.5 bg-[#FFFBEB] border-l-2 border-[#D97706] text-[#0A0A0A] whitespace-pre-wrap">
                          {h.client_comment}
                        </div>
                      )}
                      {h.admin_message && (
                        <p className="mt-1 italic text-[#525252]">"Eyvo note: {h.admin_message}"</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Title + summary */}
        <h1 className="text-3xl sm:text-4xl font-black text-[#0A0A0A] tracking-tight leading-tight break-words" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {spec.title}
        </h1>
        {spec.summary && (
          <div className="mt-4 p-4 bg-white border-l-4 border-[#0055FF]">
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">Summary</p>
            <p className="text-sm text-[#0A0A0A] mt-1.5 whitespace-pre-wrap">{spec.summary}</p>
          </div>
        )}

        {/* Sections */}
        <div className="mt-8 space-y-7">
          {(spec.sections || []).map((sec, i) => (
            <div key={i} className="bg-white border border-[#E5E5E5] p-6" data-testid={`public-section-${i}`}>
              <h2 className="text-lg font-black text-[#0A0A0A] pb-2 border-b-2 border-[#0055FF]">{i + 1}. {sec.heading}</h2>
              {/* XSS: sec.body_html is sanitised on write in routes/specs.py
                   (sanitize_rich_html). SafeHtml re-sanitises at render so
                   legacy rows also pass through the allowlist. */}
              <SafeHtml
                className="prose prose-sm max-w-none mt-3 text-[#0A0A0A]"
                style={{ lineHeight: 1.65 }}
                html={sec.body_html || "<p class='text-[#A3A3A3]'>(empty)</p>"}
              />
            </div>
          ))}
        </div>

        {/* Action strip — decision buttons only when still awaiting a decision,
            but the PDF download remains available at any time so the client
            always has a copy they can archive or forward internally. */}
        <div className="mt-10 bg-white border-2 border-[#0055FF] p-6" data-testid="public-actions">
          {!isDecided ? (
            <>
              <h3 className="text-lg font-black text-[#0A0A0A]">Your decision</h3>
              <p className="text-sm text-[#525252] mt-1">Approve this specification or send it back to Eyvo with your comments. You can also reply to the original email if you prefer.</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button
                  onClick={approve}
                  disabled={submitting}
                  className="rounded-none bg-[#059669] hover:bg-[#047857] text-white h-11 px-6 font-bold uppercase tracking-wider"
                  data-testid="public-approve"
                >
                  <CheckCircle size={16} className="mr-2" weight="bold" /> Approve
                </Button>
                <Button
                  onClick={() => setShowComment(v => !v)}
                  variant="outline"
                  className="rounded-none h-11 px-6 font-bold uppercase tracking-wider border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]"
                  data-testid="public-comment-toggle"
                >
                  <ChatCircle size={16} className="mr-2" weight="bold" /> {showComment ? "Hide" : "Send Back with Comments"}
                </Button>
                <Button
                  onClick={downloadPdf}
                  variant="outline"
                  className="rounded-none h-11 px-6 font-bold uppercase tracking-wider"
                  data-testid="public-download-pdf"
                >
                  <DownloadSimple size={16} className="mr-2" weight="bold" /> Download PDF
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-start justify-between gap-4 flex-wrap">
              {spec.status === "client_approved" ? (
                <div className="flex-1 min-w-0" data-testid="public-approved-flag">
                  <div className="bg-[#059669] text-white px-6 py-5 border-4 border-[#047857] flex items-center gap-4 mb-3">
                    <CheckCircle size={44} weight="fill" className="flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-2xl sm:text-3xl font-black uppercase tracking-[2px] leading-none">Approved</p>
                      <p className="text-xs font-bold mt-1 opacity-90 uppercase tracking-wider">
                        <span className="normal-case">v{spec.current_version || 1}</span>
                        {" · "}
                        {spec.client_approved_in_client_tz?.tz_label ? (
                          <span className="normal-case">{spec.client_approved_in_client_tz.tz_label}</span>
                        ) : fmtShort(spec.sent_at)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-[#525252]">Thank you — Eyvo has been notified and will proceed based on this document. You can still download a PDF copy for your records at any time.</p>
                </div>
              ) : (
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <ChatCircle size={22} className="text-[#D97706] flex-shrink-0 mt-0.5" weight="fill" />
                  <div>
                    <h3 className="text-base font-black text-[#0A0A0A]">Comments sent to Eyvo</h3>
                    <p className="text-sm text-[#525252] mt-1">Thank you — we've received your feedback and will revise the specification. You'll get a fresh email when <strong>v{(spec.current_version || 1) + 1}</strong> is ready for your review (this same link will keep working).</p>
                    {spec.last_comment && (
                      <div className="mt-3 text-sm text-[#0A0A0A] bg-[#FAFAFA] border border-[#E5E5E5] p-3 whitespace-pre-wrap">
                        <p className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Your comment on <span className="normal-case">v{spec.current_version || 1}</span></p>
                        {spec.last_comment}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Button
                onClick={downloadPdf}
                variant="outline"
                className="rounded-none h-10 px-4 font-bold uppercase tracking-wider flex-shrink-0"
                data-testid="public-download-pdf"
              >
                <DownloadSimple size={14} className="mr-1.5" weight="bold" /> Download PDF
              </Button>
            </div>
          )}

          {!isDecided && showComment && (
            <div className="mt-5 pt-5 border-t border-[#E5E5E5]" data-testid="public-comment-box">
              <label className="text-[10px] font-bold uppercase text-[#525252] tracking-wider">Your comments</label>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                rows={5}
                placeholder="Tell us what needs to change, what's unclear, or anything else…"
                className="w-full mt-1.5 rounded-none border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                data-testid="public-comment-textarea"
              />
              <Button
                onClick={sendBack}
                disabled={submitting || !comment.trim()}
                className="mt-3 rounded-none bg-[#D97706] hover:bg-[#B45309] text-white h-10 px-5 font-bold uppercase tracking-wider"
                data-testid="public-send-back"
              >
                <PaperPlaneTilt size={16} className="mr-2" weight="bold" /> Send back to Eyvo with comments
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-[#E5E5E5] text-center text-xs text-[#A3A3A3]">
          <p className="font-bold text-[#0A0A0A]">Eyvo, Inc.</p>
          <p className="mt-1">775 East Blithedale Avenue, Suite 332, Mill Valley, California 94941, USA</p>
          <p className="mt-0.5">888-237-9801 · sales@eyvo.com · www.eyvo.com</p>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-[#A3A3A3]">
            <ShieldCheck size={12} /> Secure confidential link · unique to your organisation
          </p>
        </div>
      </div>

      {/* Confidential bottom notice — matches the top. */}
      <div className="bg-white text-center py-3 px-4 text-[11px] font-bold text-[#DC2626] leading-tight tracking-[1px] break-words" data-testid="confidential-bottom">
        CONFIDENTIAL<br />
        Not for distribution outside of {spec.client_name}
      </div>
    </div>
  );
}
